import React, { useState, useEffect, useRef } from 'react';

import MicrophonePlugin from 'wavesurfer.js/src/plugin/microphone';
import { WaveSurfer } from 'wavesurfer-react';

import classes from './styles.module.scss';

const plugins = [
  {
    plugin: MicrophonePlugin,
  },
];

export default function WaveForm({
  containerRef,
  isRecording,
  mediaStream,
  source,
  isPlaying,
  isPaused,
  isEnded,
  isAudioEnabled,
  height,
  fullHeight,
  autoPlay,
  seek,
}) {
  const [id] = useState(Math.random().toString().split('.').join(''));

  const wavefsurferRef = useRef();

  useEffect(() => {
    const resizeWaveForm = () => {
      if (!wavefsurferRef.current) return;
      
      if (height) {
        wavefsurferRef.current.setHeight?.(height);
      } else if (fullHeight) {
        const containerHeight = containerRef.current?.clientHeight || window.innerHeight - 400;
        wavefsurferRef.current.setHeight?.(containerHeight);
      } else {
        const calculatedHeight = (containerRef.current?.clientHeight || 400) - (isAudioEnabled ? 85 : 75);
        wavefsurferRef.current.setHeight?.(calculatedHeight);
      }
      
      // Force redraw for microphone mode
      if (isRecording && wavefsurferRef.current.microphone) {
        try {
          wavefsurferRef.current.drawBuffer?.();
        } catch (e) {
          // Ignore if not available
        }
      }
    };

    window.addEventListener('resize', resizeWaveForm);
    
    // Initial resize with a small delay to ensure container is rendered
    const timeoutId = setTimeout(resizeWaveForm, 50);
    
    return () => {
      window.removeEventListener('resize', resizeWaveForm);
      clearTimeout(timeoutId);
    };
  }, [containerRef, fullHeight, height, isAudioEnabled, isRecording]);

  // Fix border bug (?) and move waveform to background
  useEffect(() => {
    const nestedWave = document.body.querySelector(`#i${id} wave wave`);
    const outerWave = document.body.querySelector(`#i${id} wave`);
    if (nestedWave) {
      nestedWave.style.borderRight = 'none';
      outerWave.style.overflowX = 'hidden';
    }

    if (outerWave) {
      outerWave.style.zIndex = 0;
    }
  }, [id]);

  useEffect(() => {
    if (!wavefsurferRef.current) {
      return undefined;
    }
    
    // Ensure WaveSurfer is empty before starting microphone
    // Microphone plugin requires empty instance
    if (isRecording) {
      try {
        // Clear any loaded source
        if (wavefsurferRef.current.isReady) {
          wavefsurferRef.current.empty();
        }
        
        const mic = wavefsurferRef.current.microphone;
        if (mic) {
          if (mediaStream) {
            mic.start(mediaStream);
          } else {
            mic.start();
          }
        } else {
          console.error('WaveForm: Microphone plugin not found');
        }
      } catch (e) {
        console.error('WaveForm: Failed to start microphone:', e);
      }
    }
    
    return () => {
      if (wavefsurferRef.current?.microphone) {
        try {
          wavefsurferRef.current.microphone.stop();
        } catch (e) { /* noop */ }
      }
    };
  }, [isRecording, mediaStream]);

  useEffect(() => {
    // Don't load source if we're in microphone recording mode
    if (source && !isRecording) {
      wavefsurferRef.current.load(source);
      wavefsurferRef.current.on('ready', () => {
        if (autoPlay) {
          wavefsurferRef.current.play();
        }
      });
    }
  }, [autoPlay, source, isRecording]);

  useEffect(() => {
    if (source && isPlaying) {
      wavefsurferRef.current.play();
    }
  }, [isPlaying, source]);

  useEffect(() => {
    if (source && isEnded) {
      wavefsurferRef.current.stop();
    }
  }, [isEnded, source]);

  useEffect(() => {
    if (source && isPaused) {
      wavefsurferRef.current.pause();
    }
  }, [isPaused, source]);

  // Mute the waveform sound
  useEffect(() => {
    if (wavefsurferRef.current) {
      wavefsurferRef.current.setVolume?.(0);
    }

    return () => {
      //  wavefsurferRef.current.destroy();
    };
  }, []);

  useEffect(() => {
    if (seek !== undefined && seek >= 0 && seek <= 1) {
      wavefsurferRef.current.seekTo(seek);
    }
  }, [seek]);

  return (
    <div 
      id={`i${id}`} 
      className={classes.WaveForm}
      style={{
        width: '100%',
        height: '100%',
        minHeight: containerRef?.current?.clientHeight || 400,
      }}
    >
      <WaveSurfer
        plugins={plugins}
        container={`#i${id}`}
        waveColor="white"
        progressColor="#808080"
        barHeight={1}
        barMinHeight={1}
        barGap={null}
        barWidth={3}
        responsive
        interact={false}
        height={fullHeight ? (containerRef?.current?.clientHeight || 400) : undefined}
        onMount={(wavesurfer) => {
          wavefsurferRef.current = wavesurfer;
          
          // Set initial height
          if (fullHeight && containerRef?.current) {
            const h = containerRef.current.clientHeight || 400;
            wavesurfer.setHeight(h);
          }
          
          // Try to start immediately if already recording
          if (isRecording && wavesurfer.microphone) {
            setTimeout(() => {
              try {
                if (mediaStream) {
                  wavesurfer.microphone.start(mediaStream);
                } else {
                  wavesurfer.microphone.start();
                }
                
                // Force redraw after starting
                setTimeout(() => {
                  try {
                    wavesurfer.drawBuffer?.();
                  } catch (e) {
                    // Ignore
                  }
                }, 200);
              } catch (e) {
                console.error('WaveForm: Failed to start on mount:', e);
              }
            }, 100);
          }
        }}
      />
    </div>
  );
}
